import React, { useState } from 'react'
import { Home } from 'lucide-react'
import {
  familyOrigin,
  hostKind,
  lastFamilySlug,
  normalizeFamilySlug,
  rememberFamilySlug,
} from '@/utils/sharedDevice'

/**
 * Parents sign in on their family's own address (<family>.housepoints.ai);
 * sign-in does not work on app.housepoints.ai. On a neutral host this asks
 * for the family address and continues there with the same path and query.
 */
export const FamilyHostGate: React.FC<{ children: React.ReactNode; title: string }> = ({ children, title }) => {
  const host = hostKind()
  const [value, setValue] = useState(lastFamilySlug())
  const [error, setError] = useState<string | null>(null)

  if (host.kind === 'family') {
    rememberFamilySlug(host.slug)
    return <>{children}</>
  }
  if (host.kind === 'other') return <>{children}</>

  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    const slug = normalizeFamilySlug(value)
    if (!slug) {
      setError('Enter your family address, for example "smith" from smith.housepoints.ai.')
      return
    }
    rememberFamilySlug(slug)
    window.location.assign(`${familyOrigin(slug)}${window.location.pathname}${window.location.search}`)
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <form onSubmit={submit} className="w-full max-w-md bg-white rounded-2xl shadow-lg p-6 sm:p-8">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-3 rounded-xl bg-indigo-100">
            <Home className="w-6 h-6 text-indigo-700" aria-hidden="true" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">{title}</h1>
        </div>
        <p className="text-gray-600 mb-5">
          Continue on your family's HousePoints address, where you sign in as a parent.
        </p>
        <label htmlFor="family-address" className="block text-sm font-semibold text-gray-800 mb-2">
          Family address
        </label>
        <div className="flex items-stretch rounded-xl border-2 border-gray-300 focus-within:border-indigo-500 overflow-hidden">
          <input
            id="family-address"
            value={value}
            onChange={(e) => {
              setValue(e.target.value)
              setError(null)
            }}
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            autoFocus
            className="flex-1 min-w-0 px-4 py-3 text-lg outline-none"
            placeholder="smith"
          />
          <span className="px-3 flex items-center bg-gray-50 text-gray-500 text-base">.housepoints.ai</span>
        </div>
        {error && (
          <p role="alert" className="text-red-700 text-sm mt-2">
            {error}
          </p>
        )}
        <button
          type="submit"
          className="mt-6 w-full min-h-[52px] rounded-xl bg-indigo-600 text-white text-lg font-semibold hover:bg-indigo-700 focus:outline-none focus:ring-4 focus:ring-indigo-300"
        >
          Continue
        </button>
      </form>
    </div>
  )
}

export default FamilyHostGate
